import SEOHead from '../components/common/SEOHead';
import { useEffect, useState } from 'react';
import GearSection from '../components/common/GearSection';
import ParallaxBlob from '../components/common/ParallaxBlob';
import Reveal from '../components/common/Reveal';
import { API_ENDPOINTS } from '../config/constants';

type BodyPhoto = {
  id: string;
  url: string;
  title?: string;
};

const AboutPage = () => {
  const [photos, setPhotos] = useState<BodyPhoto[]>([]);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.gallery}/body-photos?limit=2`);
        const json = await res.json();
        if (json.success && json.data.length >= 2) {
          setPhotos(json.data);
        } else {
          // Fallback if not enough photos
          setPhotos([
            {
              id: 'fallback1',
              url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
              title: 'Travelers exploring beautiful destinations',
            },
            {
              id: 'fallback2',
              url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
              title: 'Cultural experiences and authentic moments',
            },
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch about page photos', err);
      }
    };
    fetchPhotos();
  }, []);

  return (
    <>
      <SEOHead
        title="About Us"
        description="Meet the team behind Mermaids Of The Moment. We're passionate travelers sharing honest stories, cultural experiences, and travel inspiration from 50+ countries."
        path="/about"
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden section-padding bg-gradient-to-b from-white via-blue-50 to-indigo-50">
          <ParallaxBlob
            className="-top-24 -left-24"
            from="from-indigo-200"
            to="to-blue-200"
            width="w-80"
            height="h-80"
            speed={0.12}
          />
          <ParallaxBlob
            className="top-10 -right-24"
            from="from-pink-200"
            to="to-purple-200"
            width="w-72"
            height="h-72"
            speed={0.18}
          />

          <div className="container-max text-center relative z-10">
            <Reveal>
              <img
                src="/android-chrome-192x192.png"
                alt="Mermaids Of The Moment logo"
                className="w-16 h-16 mx-auto mb-6 rounded-2xl shadow-lg ring-1 ring-black/5"
              />
            </Reveal>
            <Reveal delayMs={80}>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 bg-gradient-to-r from-slate-900 via-indigo-800 to-slate-900 bg-clip-text text-transparent">
                About Us — Mermaids of the Moment
              </h1>
            </Reveal>
            <Reveal delayMs={140}>
              <p className="text-large text-slate-600/90 max-w-3xl mx-auto">
                Welcome to Mermaids Of The Moment, where wanderlust meets
                storytelling.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Main Content */}
        <section className="section-padding bg-gray-50">
          <div className="container-max">
            <div className="max-w-4xl mx-auto">
              {/* Story Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div className="space-y-6">
                  <div className="prose prose-lg">
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      We're a group of passionate travelers, dreamers, and
                      memory-makers who believe that every journey—whether
                      across oceans or just around the corner—holds a spark of
                      magic. Our hearts belong to the road, to the waves, to the
                      mountains, and most of all—to the moments in between.
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      This blog is our travel journal, our photo album, and our
                      connection to curious souls like you. From hidden beaches
                      to bustling city streets, we share not just where we
                      go—but how it feels to be there. 🌍✨
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <img
                    src={
                      photos[0]?.url ||
                      'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
                    }
                    alt={
                      photos[0]?.title ||
                      'Travelers exploring beautiful destinations'
                    }
                    className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent rounded-3xl"></div>
                </div>
              </div>

              {/* Values Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div className="relative order-2 lg:order-1">
                  <img
                    src={
                      photos[1]?.url ||
                      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
                    }
                    alt={
                      photos[1]?.title ||
                      'Cultural experiences and authentic moments'
                    }
                    className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-green-900/20 to-transparent rounded-3xl"></div>
                </div>

                <div className="space-y-6 order-1 lg:order-2">
                  <div className="prose prose-lg">
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      We dive deep into cultures, cuisine, and conversations.
                      You'll find honest travel tips, authentic local
                      experiences, and the kind of stories that stay with you
                      long after the trip ends.
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">
                      Whether you're here to plan your next escape, follow our
                      adventures, or simply dream a little—thank you for joining
                      us.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mission Statement */}
              <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12 text-center">
                <div className="max-w-3xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                    We are the Mermaids of the Moment.
                  </h2>
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    And this is our ocean of stories. 🧜‍♀️✨
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                    <div className="text-center">
                      <div className="text-3xl font-heading font-bold text-blue-600 mb-2">
                        50+
                      </div>
                      <div className="text-gray-600">Countries Explored</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-heading font-bold text-green-600 mb-2">
                        200+
                      </div>
                      <div className="text-gray-600">Stories Shared</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-heading font-bold text-purple-600 mb-2">
                        ∞
                      </div>
                      <div className="text-gray-600">Memories Made</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gear Section */}
        <GearSection />

        {/* Call to Action */}
        <section className="section-padding bg-gray-900 text-white">
          <div className="container-max text-center">
            <h2 className="heading-md mb-6">Ready to Join Our Journey?</h2>
            <p className="text-large text-gray-300 mb-8 max-w-2xl mx-auto">
              Follow our adventures, get inspired, and start planning your own
              magical moments around the world.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/blog"
                className="btn-primary bg-white text-gray-900 hover:bg-gray-100"
              >
                Read Our Stories
              </a>
              <a
                href="/contact"
                className="btn-secondary border-white text-white hover:bg-white hover:text-gray-900"
              >
                Get in Touch
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
